import { computed, action, observable, runInAction } from 'mobx'

import BaseListStore, { IBaseListAddOptions } from 'stores/base/BaseListStore'
import LayoutStore from 'stores/layout/LayoutStore'
import TabStore from 'stores/tab/TabStore'
import ErrorStore from 'stores/error/ErrorStore'

import photonRequestsFromActions from 'utils/photonRequestsFromActions'

import Action from './Action'
import { IActionCreatePayload } from './types'
import { IPrismaClientRequestRPC } from '@prisma/studio-types'

class ActionStore extends BaseListStore<Action, IActionCreatePayload> {
  @observable committing: boolean = false

  constructor() {
    super(Action, { idbTableName: 'actions' })
  }

  @computed
  get actions(): Action[] {
    return Object.values(this.values).filter(
      a => a.sessionId === TabStore.activeTab?.sessionId
    )
  }

  @computed
  get invalidActions(): Action[] {
    return this.actions.filter((a: Action) => !a.isValid)
  }

  /**
   * Creates a new action if needed.
   * This might merge the requested action to an existing one to keep number of actions minimal
   */
  @action
  add = (
    action: Omit<IActionCreatePayload, 'id'>,
    options: IBaseListAddOptions = {}
  ): Action => {
    const createdAction = super.add(action, options)

    this.consolidate()

    if (this.actions.length > 0) {
      TabStore.activeTab?.update({ preview: false })
      LayoutStore.updateActions({ visible: true })
    }

    return createdAction
  }

  @action
  remove = (actionId: string): Action => {
    const removedAction = super.remove(actionId)
    if (this.actions.length === 0) {
      LayoutStore.updateActions({ visible: false })
    }
    return removedAction
  }

  @action
  commit = async () => {
    if (this.invalidActions.length > 0) {
      // This should never happen
      console.error(
        'Commit failed: Some actions are invalid',
        this.invalidActions
      )
      ErrorStore.update({
        type: 'fatal',
        description:
          'Failed to commit changes because some actions are invalid',
        dump: `Invalid actions: \n${JSON.stringify(
          this.invalidActions,
          null,
          2
        )}`,
      })
      LayoutStore.updateError({ visible: true })

      return
    }

    if (!TabStore.activeTab?.session?.isScript) {
      return
    }

    const actions: Action[] = this.actions
    let requests: string[] = []
    try {
      requests = photonRequestsFromActions(actions.map(a => a.id))
    } catch (e) {
      console.error(
        'Commit failed: Unable to generate Prisma Client requests',
        e
      )
      ErrorStore.update({
        type: 'fatal',
        description: `An unexpected error occurred while saving your changes`,
        dump: `Message: ${e.message}
`,
      })

      LayoutStore.updateError({ visible: true })

      // Rethrow the error so consumer can handle it the way it wants
      throw e
    }

    runInAction(() => (this.committing = true))

    let i = 0
    try {
      // Run all requests in one transaction
      const { error } = await window.transport.request<IPrismaClientRequestRPC>(
        {
          channel: 'prisma',
          action: 'clientRequest',
          payload: {
            data: {
              query: `prisma.$transaction([ ${requests.join(',')} ])`,
            },
          },
        }
      )
      if (error) {
        throw error
      }

      // Also update model counts
      this.actions
        .filter((a: Action) => a.type === 'create')
        .forEach((a: Action) =>
          a.record?.model.update({ count: (a.record.model.count || 0) + 1 })
        )

      this.actions
        .filter((a: Action) => a.type === 'delete')
        .forEach((a: Action) =>
          a.record?.model.update({ count: (a.record.model.count || 0) - 1 })
        )

      // This will also run the script as a side-effect
      this.discard()
      runInAction(() => (this.committing = false))
    } catch (e) {
      runInAction(() => (this.committing = false))

      const model = TabStore.activeTab!.session!.script.model

      if (
        e.type === 'PrismaClientRustPanicError' ||
        e.type === 'PrismaClientUnknownRequestError'
      ) {
        ErrorStore.update({
          type: 'fatal',
          description: `An unexpected error occurred while saving your changes to the \`${model.name}\` table`,
          dump: `Message: ${e.message}

Query: \n${requests[i]}
`,
        })

        LayoutStore.updateError({ visible: true })

        // Rethrow the error so consumer can handle it the way it wants
        throw e
      }

      const errorDump = `Type: ${e.type}
Message: ${e.message}

Code: ${e.code}

Query:
${requests[i]}
`

      ErrorStore.update({
        type: 'client',
        description: `Failed to commit changes: ${e.message}`,
        dump: errorDump,
      })

      LayoutStore.updateError({ visible: true })

      // Rethrow the error so consumer can handle it the way it wants
      throw e
    }
  }

  @action
  discard = () => {
    // We cannot use ActionStore.clear() here because `discard` really means discarding RELEVANT actions
    // This means actions that affect the currently active tab / session

    const actionsToDiscard = [...this.actions] // Need to copy because removing changes indices
    for (let i = 0; i < actionsToDiscard.length; i++) {
      this.remove(actionsToDiscard[i].id)
    }

    LayoutStore.updateActions({ visible: false })
  }

  /**
   * Ensures that the number of actions are as low as possible
   *
   * More specifically,
   * 1. Merges changes to the same field in multiple actions into one
   * 2. Merges changes to multiple fields in the same record but over multiple actions into one
   * 3. Cancels out a `create` action with a `delete` action
   * 4. Cancels out any `update` actions with a `delete` action
   */
  @action
  consolidate = () => {
    const actionIds = Object.values(this.values).map(a => a.id)
    const actionIdsToDelete: Set<string> = new Set() // Actions that should be deleted after consolidation (because they have been absorbed / handled / canceled out)

    const _consolidateOne = (actionId: string, idx: number): void => {
      // What this function does is it takes an action at an index and finds the first action to its right that it can merge into itself.
      // If it does find such an action, it merges that action into itself and then deletes the one it found. Then it recurses until there are no more actions to absorb.
      // We move from left to right to make sure that newer actions on the same field overwrite older actions

      const action = this.get(actionId) // Action that will absorb
      if (!action) {
        return
      }

      // Try to find an action that can be merged into the one provided
      const indexOfActionToAbsorb = actionIds.findIndex((aId, i) => {
        if (i <= idx) {
          // Only look for actions it your right
          return false
        }

        const a = this.get(aId) // Action that is a candidate to be absorbed
        if (!a) {
          return false
        }

        if (
          a.sessionId !== action.sessionId ||
          a.recordId !== action.recordId
        ) {
          // Actions on different records / sessions are always different
          return false
        }

        if (a.type === action.type) {
          // Actions with the same type are always mergeable.
          // This is true because create and delete actions will never make it here thanks to the previous check
          return true
        }

        // If everything fails, then there indeed is no other action to absorb
        return false
      })

      if (indexOfActionToAbsorb === -1) {
        // No more actions to absorb, all done
        return
      }

      // If a suitable action is found, absorb its value , delete it, and recurse
      const actionToAbsorb = this.get(actionIds[indexOfActionToAbsorb])
      if (!actionToAbsorb) {
        return
      }

      action.update({
        value: {
          ...action.value,
          ...actionToAbsorb.value,
        },
      })

      actionIdsToDelete.add(actionIds[indexOfActionToAbsorb]) // Mark this action to be deleted from ActionStore as well, since it has been absorbed
      actionIds.splice(indexOfActionToAbsorb, 1)
      return _consolidateOne(actionId, idx) // Continue absorbing
    }

    actionIds.forEach(_consolidateOne)

    const _cancelCreatesAndUpdates = (actionId: string) => {
      const action = this.get(actionId)
      if (!action) {
        return
      }

      if (action.type !== 'delete') {
        return
      }

      actionIds.forEach(aId => {
        const a = this.get(aId)
        if (!a) return

        if (a && a.type !== 'delete' && a.recordId === action.recordId) {
          // If there is another action on the same record, get rid of them, because the `delete` action would override it
          // Except other deletes. We can only remove them if this is not a committed record
          actionIdsToDelete.add(aId)
        }

        if (!a.record?.isCommitted) {
          // For non committed records, delete yourself too
          actionIdsToDelete.add(actionId)
        }
      })
    }
    actionIds.forEach(_cancelCreatesAndUpdates)

    actionIdsToDelete.forEach(aId => this.remove(aId))
  }
}

export default new ActionStore()
